import { preloadQuery } from "@relay";
import { createFileRoute, useLinkProps, useNavigate, useRouter } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { usePollingQuery } from "@hooks/usePollingQuery";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useEffect, useRef, useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import z from "zod";
import { useCheckoutStore } from "~/stores/checkout-store";
import { IllustrationOpeningAnimation } from "@assets/IllustrationOpeningAnimation";
import { PageContainer } from "~/components/layout/PageContainer";
import { Button } from "@shadcn/ui/button";
import { MultipleCompartmentsPage } from "./-MultipleCompartmentsPage";
import { OpenDoorPage } from "./-OpenDoorPage";
import { instantBuyCheckoutQuery } from "./__generated__/instantBuyCheckoutQuery.graphql";
import { instantBuyOpenSpaceMutation } from "./__generated__/instantBuyOpenSpaceMutation.graphql";
import { useI18nContext } from "@i18n/i18n-react";
import { AppHeader } from "~/components/layout/AppHeader";

const PURCHASE_POLLING_TIMEOUT_MS = 15_000;

const purchaseQuery = graphql`
	query instantBuyCheckoutQuery($checkoutId: String!) {
		instantBuy(by: { clientReferenceId: $checkoutId }) @required(action: THROW) {
			id
			status
			hub {
				id
				meta @required(action: THROW) {
					id
					name
				}
			}
			lineItems @required(action: THROW) {
				id
				productInstance {
					id
					reservation {
						id
						space {
							id
							frame
						}
					}
					retrievedWithId
				}
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/pick-up/instant-buy")({
	beforeLoad: () => {
		document.title = "bacehub | Instat Buy";
	},
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			checkoutId: z.string(),
			lineItemId: z.string().optional(),
		})
	),
	loaderDeps: ({ search: { checkoutId, lineItemId } }) => ({ checkoutId, lineItemId }),
	loader: async (ctx) => {
		if (!ctx.preload) {
			useCheckoutStore.getState().removeCheckout(ctx.deps.checkoutId);
		}
		return preloadQuery<instantBuyCheckoutQuery>(ctx.context.relayEnvironment, purchaseQuery, {
			checkoutId: ctx.deps.checkoutId,
		});
	},
});

export type LineItem = {
	id: string;
	side: string;
};

export default function RouteComponent() {
	const { checkoutId, lineItemId } = Route.useSearch();
	const { instantBuy } = usePreloadedQuery<instantBuyCheckoutQuery>(purchaseQuery, Route.useLoaderData());
	const i18n = useI18nContext().LL["new-ui"];

	if (instantBuy.status !== "PURCHASED" || instantBuy.lineItems.length === 0) {
		return <InstantBuyPurchaseProcessingPage checkoutId={checkoutId} />;
	}

	const lineItems: LineItem[] = instantBuy.lineItems
		.filter((lineItem) => lineItem.productInstance.retrievedWithId !== undefined)
		.map((lineItem) => {
			const space = lineItem.productInstance.reservation.space;
			const side = space.frame.at(0);
			if (!side) {
				toast.error(i18n.COULD_NOT_DETERMINE_SIDE());
			}
			return {
				id: lineItem.id,
				side: side ?? "",
			};
		})
		.sort((a, b) => b.side.localeCompare(a.side));

	if (lineItems.length === 0) {
		toast.error(i18n.TRANSACTION_NO_LINE_ITEM());
		return;
	}

	if (lineItems.length > 1) {
		const [first, second] = lineItems;
		if (!first || !second) {
			throw new Error("Should not happen because then the above condition cannot succeed");
		}
		if (lineItemId) {
			const lineItemIndex = lineItems.findIndex((lineItem) => lineItem.id === lineItemId);
			const lineItem = lineItems[lineItemIndex];
			const nextLineItem = lineItems[lineItemIndex + 1];
			if (!lineItem) {
				toast.error(i18n.LINE_ITEM_NOT_FOUND());
				return;
			}
			return (
				<InstantBuyOpenDoorPage
					hubId={instantBuy.hub.id}
					hubName={instantBuy.hub.meta.name}
					transactionId={instantBuy.id}
					lineItem={lineItem}
					nextLineItem={nextLineItem}
				/>
			);
		}
		return <MultipleCompartmentsPage lineItem={first} />;
	}

	const lineItem = lineItems[0]!;

	return (
		<InstantBuyOpenDoorPage
			hubId={instantBuy.hub.id}
			hubName={instantBuy.hub.meta.name}
			transactionId={instantBuy.id}
			lineItem={lineItem}
			nextLineItem={undefined}
		/>
	);
}

type InstantBuyPurchaseProcessingPageProps = {
	checkoutId: string;
};

type InstantBuyPurchaseStatusPollingProps = {
	checkoutId: string;
	onPurchased: () => void;
};

const InstantBuyPurchaseStatusPolling = (props: InstantBuyPurchaseStatusPollingProps) => {
	const { checkoutId, onPurchased } = props;

	usePollingQuery<instantBuyCheckoutQuery, true>({
		query: purchaseQuery,
		variables: {
			checkoutId,
		},
		createEvaluator:
			() =>
			({ instantBuy }) =>
				instantBuy.status === "PURCHASED" && instantBuy.lineItems.length > 0 ? { type: "complete", result: true } : { type: "continue" },
		onComplete: onPurchased,
		intervalMs: 1000,
		minTimeMs: 500,
		sessionKey: checkoutId,
	});

	return null;
};

const InstantBuyPurchaseProcessingPage = (props: InstantBuyPurchaseProcessingPageProps) => {
	const { checkoutId } = props;
	const i18n = useI18nContext().LL["new-ui"];
	const router = useRouter();
	const hasInvalidatedRef = useRef(false);
	const hasShownTimeoutToastRef = useRef(false);
	const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
	const [pollingSession, setPollingSession] = useState(0);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setIsPollingTimedOut(true);
		}, PURCHASE_POLLING_TIMEOUT_MS);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [pollingSession]);

	useEffect(() => {
		if (!isPollingTimedOut || hasShownTimeoutToastRef.current) {
			return;
		}

		hasShownTimeoutToastRef.current = true;
		toast.error(i18n.PURCHASE_STILL_PROCESSING());
	}, [isPollingTimedOut, i18n]);

	const handlePollingTimeoutRetry = () => {
		hasShownTimeoutToastRef.current = false;
		setIsPollingTimedOut(false);
		setPollingSession((current) => current + 1);
		void router.invalidate();
	};

	const handlePurchased = () => {
		if (hasInvalidatedRef.current) {
			return;
		}

		hasInvalidatedRef.current = true;
		void router.invalidate();
	};

	return (
		<PageContainer className="px-6">
			{!isPollingTimedOut ? (
				<InstantBuyPurchaseStatusPolling key={pollingSession} checkoutId={checkoutId} onPurchased={handlePurchased} />
			) : null}
			<div className="-mx-6">
				<AppHeader showHelpButton={true} />
			</div>
			<h1 className="text-2xl font-breit">{i18n.OPENING_SHIPMENT_DETAILS()}</h1>
			<p className="text-sm text-muted-foreground">{i18n.PURCHASE_STILL_PROCESSING()}</p>
			<div className="h-[468px] flex items-center justify-center">
				<IllustrationOpeningAnimation className="w-[163px]" />
			</div>
			{isPollingTimedOut ? (
				<Button className="w-full" size="lg" onClick={handlePollingTimeoutRetry}>
					{i18n.CONTINUE()}
				</Button>
			) : null}
		</PageContainer>
	);
};

type InstantBuyOpenDoorPageProps = {
	hubId: string;
	hubName: string;
	transactionId: string;
	lineItem: LineItem;
	nextLineItem: LineItem | undefined;
};

const InstantBuyOpenDoorPage = (props: InstantBuyOpenDoorPageProps) => {
	const { hubId, hubName, transactionId, lineItem, nextLineItem } = props;
	const { checkoutId } = Route.useSearch();
	const navigate = useNavigate();
	const router = useRouter();
	const i18n = useI18nContext().LL["new-ui"];

	const [openSpace, isOpenSpaceLoading] = useAsyncMutation<instantBuyOpenSpaceMutation>(graphql`
		mutation instantBuyOpenSpaceMutation($lineItemId: UUID!) {
			lineItemRetrieve(input: { lineItemId: $lineItemId }) {
				id
			}
		}
	`);

	const returnPath = useLinkProps({
		from: "/$hubMetaName/pick-up/instant-buy",
		to: "/$hubMetaName/pick-up/instant-buy",
		search: {
			checkoutId,
			lineItemId: nextLineItem?.id,
		},
	});
	const problemPath = useLinkProps({
		from: "/$hubMetaName/pick-up/instant-buy",
		to: "/$hubMetaName/pick-up/instant-buy",
		search: {
			checkoutId,
			lineItemId: lineItem.id,
		},
	});
	const finishPath = useLinkProps({
		from: "/$hubMetaName/pick-up/instant-buy",
		to: "/$hubMetaName/pick-up/finish",
		search: {
			transactionId,
		},
	});

	const onOpenDoor = async () => {
		if (isOpenSpaceLoading) {
			return;
		}

		try {
			const response = await openSpace({
				variables: {
					lineItemId: lineItem.id,
				},
			});
			router.invalidate();
			navigate({
				from: "/$hubMetaName/pick-up/instant-buy",
				to: "/$hubMetaName/pick-up/opening",
				search: {
					finishPath: nextLineItem ? returnPath.href : finishPath.href,
					problemPath: problemPath.href,
					unlockRequestId: response.lineItemRetrieve.id,
				},
			});
		} catch (error) {
			console.error(error);
			setTimeout(() => {
				if (window.location.pathname.includes("/pick-up/instant-buy")) {
					toast.error(i18n.OPEN_DOOR_FAILED());
				}
			}, 3000);
		}
	};

	return (
		<OpenDoorPage
			hubId={hubId}
			hubName={hubName}
			side={lineItem.side}
			skipHubCheck={true}
			isLoading={isOpenSpaceLoading}
			onOpenDoor={onOpenDoor}
		/>
	);
};
